import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { MOCK_SITE_SETTINGS } from "../data/homamData";

const HomamContext = createContext();

export const HomamProvider = ({ children }) => {
  // Load cached status from localStorage for instantaneous rendering without flash
  const [isHomamActive, setIsHomamActive] = useState(() => {
    const cached = localStorage.getItem("homam_active_status");
    return cached === "false" ? false : true;
  });
  const [whatsappNumber, setWhatsappNumber] = useState(() => {
    const cached = localStorage.getItem("mandir_whatsapp_number");
    if (cached && !cached.includes("9080845897")) return cached;
    return "916369482332";
  });
  const [formattedWhatsAppNumber, setFormattedWhatsAppNumber] = useState(() => {
    const cached = localStorage.getItem("mandir_whatsapp_formatted");
    if (cached && !cached.includes("90808")) return cached;
    return "+91 63694 82332";
  });
  const [loadingStatus, setLoadingStatus] = useState(true);

  const fetchStatus = async () => {
    try {
      const endpoint = API_ENDPOINTS.HOMAM_SETTINGS || `${API_ENDPOINTS.API_BASE}/homam/settings` || "http://localhost:5000/api/homam/settings";
      const res = await axios.get(endpoint);
      // Strictly verify response is an actual JSON object with boolean isHomamActive
      if (res.data && typeof res.data === "object") {
        if (typeof res.data.isHomamActive === "boolean") {
          const active = res.data.isHomamActive;
          setIsHomamActive(active);
          localStorage.setItem("homam_active_status", String(active));
        }
        if (res.data.whatsappNumber) {
          const clean = String(res.data.whatsappNumber).replace(/\D/g, "");
          const fmt = res.data.formattedWhatsAppNumber || (clean.length === 10 ? `+91 ${clean.slice(0, 5)} ${clean.slice(5)}` : `+${clean}`);
          setWhatsappNumber(clean);
          setFormattedWhatsAppNumber(fmt);
          localStorage.setItem("mandir_whatsapp_number", clean);
          localStorage.setItem("mandir_whatsapp_formatted", fmt);
          if (typeof window !== "undefined") {
            window.__MANDIR_WHATSAPP_NUMBER__ = clean;
            window.__MANDIR_WHATSAPP_FORMATTED__ = fmt;
          }
          if (MOCK_SITE_SETTINGS) {
            MOCK_SITE_SETTINGS.whatsappNumber = clean;
            MOCK_SITE_SETTINGS.whatsappDisplayNumber = fmt;
          }
        }
      }
    } catch (err) {
      console.warn("Could not fetch Homam status from backend, keeping active state:", err.message);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    // Cross-tab and real-time synchronization
    const handleStorageChange = (e) => {
      if (e.key === "mandir_whatsapp_number" && e.newValue) {
        setWhatsappNumber(e.newValue);
        if (typeof window !== "undefined") window.__MANDIR_WHATSAPP_NUMBER__ = e.newValue;
        if (MOCK_SITE_SETTINGS) MOCK_SITE_SETTINGS.whatsappNumber = e.newValue;
      }
      if (e.key === "mandir_whatsapp_formatted" && e.newValue) {
        setFormattedWhatsAppNumber(e.newValue);
        if (typeof window !== "undefined") window.__MANDIR_WHATSAPP_FORMATTED__ = e.newValue;
        if (MOCK_SITE_SETTINGS) MOCK_SITE_SETTINGS.whatsappDisplayNumber = e.newValue;
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    }
  }, []);

  const updateHomamActive = (newStatus) => {
    const active = Boolean(newStatus);
    setIsHomamActive(active);
    localStorage.setItem("homam_active_status", String(active));
  };

  const updateWhatsAppNumber = (newNumber, formatted = "") => {
    const clean = String(newNumber || "").replace(/\D/g, "");
    const fmt = formatted || (clean.length === 10 ? `+91 ${clean.slice(0, 5)} ${clean.slice(5)}` : `+${clean}`);
    setWhatsappNumber(clean);
    setFormattedWhatsAppNumber(fmt);
    localStorage.setItem("mandir_whatsapp_number", clean);
    localStorage.setItem("mandir_whatsapp_formatted", fmt);
    if (typeof window !== "undefined") {
      window.__MANDIR_WHATSAPP_NUMBER__ = clean;
      window.__MANDIR_WHATSAPP_FORMATTED__ = fmt;
      try {
        window.dispatchEvent(new CustomEvent("mandir_whatsapp_updated", { detail: { clean, fmt } }));
      } catch (_) {}
    }
    if (MOCK_SITE_SETTINGS) {
      MOCK_SITE_SETTINGS.whatsappNumber = clean;
      MOCK_SITE_SETTINGS.whatsappDisplayNumber = fmt;
    }
  };

  return (
    <HomamContext.Provider
      value={{
        isHomamActive,
        updateHomamActive,
        whatsappNumber,
        formattedWhatsAppNumber,
        updateWhatsAppNumber,
        refreshHomamStatus: fetchStatus,
        loadingStatus,
      }}
    >
      {children}
    </HomamContext.Provider>
  );
};

export const useHomam = () => {
  const context = useContext(HomamContext);
  if (!context) {
    const cachedNum = typeof window !== "undefined" ? localStorage.getItem("mandir_whatsapp_number") : null;
    const cachedFmt = typeof window !== "undefined" ? localStorage.getItem("mandir_whatsapp_formatted") : null;
    return {
      isHomamActive: true,
      updateHomamActive: () => {},
      whatsappNumber: cachedNum || "919080845897",
      formattedWhatsAppNumber: cachedFmt || "+91 90808 45897",
      updateWhatsAppNumber: () => {},
      refreshHomamStatus: () => {},
      loadingStatus: false,
    };
  }
  return context;
};

export default HomamContext;
